/**
 * Partner-organization signup against the backend (`POST /api/auth/signup`), for the sign-up form on
 * the website's sign-in page. A committed signup waits in PawsitiveFuture's approval queue; once it is
 * approved, its contact signs in on the same page with an emailed code.
 */
import { apiBase } from './api';

export interface OrgType {
  readonly value: string;
  readonly label: string;
}

/** Values are lowercase to match the DAL business-type mapping. */
export const ORG_TYPES: readonly OrgType[] = [
  { value: 'hospital', label: 'Hospital / Veterinary Clinic' },
  { value: 'distributor', label: 'Distributor (Food Bank, Relief Center)' },
  { value: 'partner', label: 'Partner Organization' },
  { value: 'network', label: 'Network / Umbrella Organization' },
];

/**
 * The org types PawsitiveFuture pays out to: a clinic bills for the care it delivers and an umbrella
 * org disburses to its members, so holding their Tax ID up front shortens payout setup. This gates
 * COLLECTION only — the Tax ID is optional for every org type, here, in the API and in the DAL.
 */
export const PAYOUT_ORG_TYPES: readonly string[] = ['hospital', 'network'];

export interface SignupForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  orgName: string;
  businessTypes: string[];
  address: string;
  taxId: string;
}

export class SignupError extends Error {}

/**
 * Submit a signup. The route answers a refusal with the legacy `{ error: CODE, message }` envelope,
 * so the human sentence is `message`; a body that is not JSON reads as an unreachable server.
 */
export async function submitSignup(form: SignupForm): Promise<void> {
  const collectsTaxId = form.businessTypes.some((type) => PAYOUT_ORG_TYPES.includes(type));
  let res: Response;
  try {
    res = await fetch(`${apiBase}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        orgName: form.orgName.trim(),
        businessTypes: form.businessTypes,
        address: form.address.trim() || undefined,
        // A Tax ID typed under a payout type stays in the form if that type is deselected, so
        // re-selecting it restores the value; it is only SENT while a payout type is selected.
        taxId: collectsTaxId ? form.taxId.trim() || undefined : undefined,
      }),
    });
  } catch {
    throw new SignupError('Could not reach the server. Please check your connection and try again.');
  }
  if (res.ok) return;

  let body: { message?: unknown; error?: unknown } | null = null;
  try { body = await res.json(); } catch { body = null; }
  if (!body) throw new SignupError('Could not reach the server. Please try again.');
  const { message, error } = body;
  if (typeof message === 'string' && message) throw new SignupError(message);
  throw new SignupError(typeof error === 'string' && error ? error : 'Signup failed. Please try again.');
}
